<template>
  <BContentBox>
    <template #content>
      <div
        v-if="selectedObject.length <= 0"
      >
        <SelectObject
          @select-object="selectObject"
        />
        <div class="pl-600 pb-300">
          <div class="pt-400 pb-100">
            <h3>{{ $t(`corpSettingApi.salesforce.subTabs.relationUser`) }}</h3>
          </div>
          <div class="header-text">
            <span>{{ $t("salesforceSettings.syncObjectType.userMappingDesc") }}</span>
          </div>
          <div class="header-text">
            <span>{{ $t("sfObjectMappings.targetObjectLabel") }}</span>
          </div>
          <div
            class="select-user"
            @click="() => selectMapping('salesforce', 'salesforceRelationUser')"
          >
            <span>User</span>
          </div>
        </div>
        <div class="pl-600">
          <div class="pt-400 pb-300">
            <h3>{{ $t(`corpSettingApi.salesforce.subTabs.syncLead`) }}</h3>
          </div>
          <div class="explain">
            <span>{{ $t("salesforceSettings.syncObjectType.syncObjectTypeDesc") }}</span>
          </div>
          <BListItem
            class="radio-area mb-200"
          >
            <div class="radio-group">
              <BRadio
                v-for="syncSfObjectValue in salesforceApiInfo.syncSfObjects"
                :key="syncSfObjectValue"
                v-model="syncSfObject"
                class="radio-item"
                :label="syncSfObjectValue"
              >
                {{ $t(`salesforceSettings.syncObjectType.enum.${convertToCamelcaseKeys(syncSfObjectValue)}`) }}{{ $t('general.from') }}{{ $t('general.create.text') }}
              </BRadio>
            </div>
          </BListItem>
          <BBtn
            type="primary"
            @click="updateSyncSfObject"
          >
            <span>{{ $t('general.save.text') }}</span>
          </BBtn>
        </div>
        <TodoSync />
      </div>
      <div v-else>
        <MappingColumn
          :selected-object="selectedObject"
          @back="backAction"
        />
      </div>
    </template>
  </BContentBox>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { SfSalesforceSettingsService } from '@/api/user/resources/salesforce/salesforce_settings';
import { SfObjectMappingService } from '@/api/user/resources/salesforce/sf_object_mapping';
import MappingColumn from '@/components/organisms/user/setting/api/SalesforceApiMap/MappingColumn.vue';
import SelectObject from '@/components/organisms/user/setting/api/SalesforceApiMap/SelectObject.vue';
import TodoSync from '@/components/organisms/user/setting/api/TodoSync.vue';
import { useToast } from '@/composable/toast';

type TProps = {
  salesforceApiInfo: {
    syncSfObject: string,
    syncSfObjects: string[],
  },
};
const props = defineProps<TProps>();

const emit = defineEmits(['change-subtabs']);
const { successToast } = useToast();
const i18n = useI18n();
const apiService = new SfObjectMappingService();
const apiSalesforceSettingsService = new SfSalesforceSettingsService();
const salesforceSettingLoading = ref(true);
const sfObjectMappingsKeys = ref([]);
const selectedObject = ref('');
const syncSfObject = ref<string>('lead');

const selectMapping = (tabType: string, pageName: string) => {
  emit('change-subtabs', tabType, pageName);
};

const selectObject = (object: string) => {
  selectedObject.value = object;
};

const backAction = () => {
  selectedObject.value = '';
};

const updateSyncSfObject = async () => {
  await apiSalesforceSettingsService.updateSyncSfObject({ syncSfObject: syncSfObject.value });
  successToast(i18n.t('general.success.to', { action: i18n.t('general.update.text') }));
};

const convertToCamelcaseKeys = (dataType) => {
  if (dataType.indexOf('_') == -1) {
    return dataType;
  }
  return dataType.split('_').map((word, index) => {
    if (index === 0) {
      return word.toLowerCase();
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join('');
};

if (props.salesforceApiInfo.syncSfObject.length) {
  syncSfObject.value = props.salesforceApiInfo.syncSfObject;
}

onMounted(async () => {
  const result = await apiService.getSfObjectMappingsKeys();
  sfObjectMappingsKeys.value = result.data.sfObjectMappingsKeys;
  salesforceSettingLoading.value = false;
});
</script>

<style lang="scss" scoped>
.content {
  width: 100%;
}

.header-text {
  color: $textcolor-light;
  margin-bottom: 24px;
  white-space: pre-wrap;
}

.mapping-key-text {
  font-weight: bold;
}

.select-colum {
  > .el-select {
    width: 100%;
  }
}

.hr {
  border-bottom: 1px solid $bdcolor-base;
  margin-bottom: 8px;
}

.select-user {
  cursor: pointer;

  &:first-child {
    margin-top: -8px;
  }
  &:not(:last-child) {
    border-bottom: 1px solid $bdcolor-base;
  }
  span {
    font-weight: bold;
  }
}

.radio-group {
  display: flex;
  flex-direction: column;
  label {
    margin: 6px 0;
  }
}

.explain {
  color: $concrete-dark;
  margin-bottom: 20px;
  white-space: pre-wrap;
}
</style>
